import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { fetchProfile } from '../api/auth/auth';
import {
  clearAuthSession,
  getAccessToken,
  getStoredUser,
  setStoredUser,
} from '../utils/tokenStorage';

const UserContext = createContext(undefined);

export const UserProvider = ({ children }) => {
  const [user, setUserState] = useState(() => getStoredUser());

  const refreshUser = useCallback(() => {
    setUserState(getStoredUser());
  }, []);

  const setUser = useCallback((nextUser) => {
    setUserState(nextUser || null);
    setStoredUser(nextUser || null);
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
    clearAuthSession();
  }, []);

  useEffect(() => {
    // Keep state in sync when localStorage changes (other tabs/windows)
    const onStorage = (event) => {
      if (!event) return;
      refreshUser();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshUser]);

  /** Merge UserProfile (e.g. jobTitle) from API when session user was saved before backend attached `profile`. */
  useEffect(() => {
    if (!getAccessToken()) return;
    const stored = getStoredUser();
    if (!stored) return;
    const job = String(stored.profile?.jobTitle ?? '').trim();
    if (job) return;

    let cancelled = false;
    fetchProfile()
      .then((res) => {
        if (cancelled) return;
        const next = res?.data?.user;
        if (!next) return;
        const sid = String(stored._id ?? stored.id ?? '');
        const nid = String(next._id ?? next.id ?? '');
        if (!sid || sid !== nid) return;
        const merged = {
          ...stored,
          ...next,
          profile: next.profile ?? stored.profile,
          roles: next.roles ?? stored.roles,
          modules: next.modules ?? stored.modules,
          permissions: next.permissions ?? stored.permissions,
        };
        setUserState(merged);
        setStoredUser(merged);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      clearUser,
      refreshUser,
      isAuthenticated: Boolean(user),
    }),
    [user, setUser, clearUser, refreshUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return ctx;
};

